import { useId, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLiveQuery } from 'dexie-react-hooks';
import { ACTIVITY_KEYS, isWallTime } from '@kraftwerk/shared';
import type { Activity } from '@kraftwerk/shared';
import { db } from '../../db/local.js';
import { deleteEvent, saveEvent } from '../../db/calendar.js';
import { listMembers } from '../../db/shopping.js';
import type { LocalHouseholdMember } from '../../db/local.js';
import type { AgendaItem } from '../../db/calendar.js';
import type { NoteEventPreset } from '../journal/DayNotes.js';

/**
 * Einen eigenen Termin anlegen oder ändern — privat oder im Haushalt, ganztägig
 * oder mit Uhrzeit, optional als Aktivität (zählt dann in der Bilanz).
 */
export function EventEditor({
  item,
  defaultDate,
  householdId,
  onWriteNote,
  onClose,
}: {
  item: AgendaItem | null;
  defaultDate: string;
  householdId: string | null;
  onWriteNote: (preset: NoteEventPreset) => void;
  onClose: () => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  const ids = {
    title: useId(),
    date: useId(),
    time: useId(),
    duration: useId(),
    end: useId(),
    location: useId(),
    note: useId(),
    activity: useId(),
    activityMin: useId(),
  };
  const [title, setTitle] = useState(item?.title ?? '');
  const [date, setDate] = useState(item?.date ?? defaultDate);
  const [allDay, setAllDay] = useState(item ? item.time === null : false);
  const [time, setTime] = useState(item?.time ?? '18:00');
  const [duration, setDuration] = useState(String(item?.durationMin ?? 60));
  const [endDate, setEndDate] = useState(item?.endDate ?? '');
  const [location, setLocation] = useState(item?.location ?? '');
  const [note, setNote] = useState(item?.note ?? '');
  const [activity, setActivity] = useState<Activity | ''>(item?.activity ?? '');
  const [activityMin, setActivityMin] = useState(
    String(item?.activityMin ?? item?.durationMin ?? 60),
  );
  const [shared, setShared] = useState(item?.kind === 'household');
  const [forUserIds, setForUserIds] = useState<string[]>(item?.forUserIds ?? []);
  // Für wen (E16): die Mitglieder des Haushalts, Kinder eingeschlossen.
  const members = useLiveQuery(
    () => listMembers(householdId, db),
    [householdId],
    [] as LocalHouseholdMember[],
  );
  const [cancelled, setCancelled] = useState(item?.cancelled ?? false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const minutes = (value: string) => {
    const parsed = Number(value);
    return Number.isInteger(parsed) && parsed > 0 && parsed <= 24 * 60 ? parsed : null;
  };
  const valid = title.trim() !== '' && date !== '' && (allDay || isWallTime(time));

  return (
    <form
      aria-labelledby="event-editor-heading"
      className="kw-tile flex flex-col gap-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (!valid) return;
        void saveEvent(
          {
            title,
            date,
            time: allDay ? null : time,
            durationMin: allDay ? null : minutes(duration),
            endDate: allDay && endDate ? endDate : null,
            location,
            note,
            activity: activity || null,
            activityMin: activity ? minutes(activityMin) : null,
            cancelled,
            shared,
            forUserIds: shared ? forUserIds : [],
          },
          item && (item.kind === 'event' || item.kind === 'household')
            ? { kind: item.kind, id: item.id }
            : null,
          householdId,
          db,
        ).then(onClose);
      }}
    >
      <h2 id="event-editor-heading" className="font-display text-xl font-bold">
        {item ? t('calendar.editor.edit') : t('calendar.editor.new')}
      </h2>

      <label htmlFor={ids.title} className="flex flex-col gap-1">
        <span className="kw-label">{t('calendar.editor.title')}</span>
        <input
          id={ids.title}
          value={title}
          maxLength={200}
          onChange={(event) => setTitle(event.target.value)}
          className="kw-field"
          required
        />
      </label>

      <div className="flex flex-wrap items-end gap-2">
        <label htmlFor={ids.date} className="flex w-40 flex-col gap-1">
          <span className="kw-label">{t('calendar.editor.date')}</span>
          <input
            id={ids.date}
            type="date"
            value={date}
            onChange={(event) => setDate(event.target.value)}
            className="kw-field"
            required
          />
        </label>
        <button
          type="button"
          aria-pressed={allDay}
          onClick={() => setAllDay(!allDay)}
          className="kw-toggle"
        >
          {t('calendar.allDayLong')}
        </button>
      </div>

      {allDay ? (
        <label htmlFor={ids.end} className="flex flex-col gap-1">
          <span className="kw-label">{t('calendar.editor.endDate')}</span>
          <input
            id={ids.end}
            type="date"
            value={endDate}
            min={date}
            onChange={(event) => setEndDate(event.target.value)}
            className="kw-field"
          />
        </label>
      ) : (
        <div className="flex gap-2">
          <label htmlFor={ids.time} className="flex w-28 flex-col gap-1">
            <span className="kw-label">{t('calendar.editor.time')}</span>
            <input
              id={ids.time}
              type="time"
              value={time}
              onChange={(event) => setTime(event.target.value)}
              className="kw-field"
            />
          </label>
          <label htmlFor={ids.duration} className="flex w-28 flex-col gap-1">
            <span className="kw-label">{t('calendar.editor.duration')}</span>
            <input
              id={ids.duration}
              inputMode="numeric"
              value={duration}
              onChange={(event) => setDuration(event.target.value)}
              className="kw-field"
            />
          </label>
        </div>
      )}

      <label htmlFor={ids.location} className="flex flex-col gap-1">
        <span className="kw-label">{t('calendar.editor.location')}</span>
        <input
          id={ids.location}
          value={location}
          maxLength={200}
          onChange={(event) => setLocation(event.target.value)}
          className="kw-field"
        />
      </label>

      <ActivityFields
        ids={ids}
        activity={activity}
        minutes={activityMin}
        onActivity={setActivity}
        onMinutes={setActivityMin}
      />

      <label htmlFor={ids.note} className="flex flex-col gap-1">
        <span className="kw-label">{t('calendar.editor.note')}</span>
        <textarea
          id={ids.note}
          value={note}
          rows={2}
          maxLength={2000}
          onChange={(event) => setNote(event.target.value)}
          className="kw-field"
        />
      </label>

      <div className="flex flex-wrap gap-2">
        {householdId ? (
          <button
            type="button"
            aria-pressed={shared}
            onClick={() => setShared(!shared)}
            className="kw-toggle"
          >
            {t('calendar.editor.shared')}
          </button>
        ) : null}
        {item ? (
          <button
            type="button"
            aria-pressed={cancelled}
            onClick={() => setCancelled(!cancelled)}
            className="kw-toggle"
          >
            {t('calendar.cancelled')}
          </button>
        ) : null}
      </div>

      {shared && members.length > 1 ? (
        <fieldset className="flex flex-col gap-1">
          <legend className="kw-label">{t('calendar.editor.forWhom')}</legend>
          <div className="flex flex-wrap gap-2">
            {members.map((member) => (
              <button
                key={member.userId}
                type="button"
                aria-pressed={forUserIds.includes(member.userId)}
                onClick={() =>
                  setForUserIds(
                    forUserIds.includes(member.userId)
                      ? forUserIds.filter((id) => id !== member.userId)
                      : [...forUserIds, member.userId],
                  )
                }
                className="kw-toggle"
              >
                {member.displayName}
              </button>
            ))}
          </div>
          <span className="text-xs text-ink-muted">{t('calendar.editor.forWhomHint')}</span>
        </fieldset>
      ) : null}

      <div className="flex gap-2">
        <button type="submit" disabled={!valid} className="kw-btn flex-1">
          {t('calendar.editor.save')}
        </button>
        <button type="button" onClick={onClose} className="kw-btn kw-btn-quiet flex-1">
          {t('nutrition.library.cancel')}
        </button>
      </div>

      {item && (item.kind === 'event' || item.kind === 'household') ? (
        <button
          type="button"
          onClick={() =>
            onWriteNote({
              kind: item.kind as 'event' | 'household',
              id: item.id,
              title: item.title,
              date: item.date,
            })
          }
          className="kw-btn kw-btn-quiet kw-btn-small"
        >
          {t('notes.writeAbout')}
        </button>
      ) : null}

      {item && (item.kind === 'event' || item.kind === 'household') ? (
        confirmDelete ? (
          <div className="flex flex-col gap-2 rounded-xl border border-danger p-3">
            <p className="text-sm">{t('calendar.editor.deleteWarning')}</p>
            <button
              type="button"
              onClick={() =>
                void deleteEvent(item.kind as 'event' | 'household', item.id, db).then(onClose)
              }
              className="kw-btn kw-btn-small border-danger text-danger"
            >
              {t('calendar.editor.delete')}
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setConfirmDelete(true)}
            className="kw-btn kw-btn-quiet kw-btn-small"
          >
            {t('calendar.editor.delete')}
          </button>
        )
      ) : null}
    </form>
  );
}

/** Aktivität und Dauer — auch für eingelesene Termine (FeedEventSheet). */
export function ActivityFields({
  ids,
  activity,
  minutes,
  onActivity,
  onMinutes,
}: {
  ids: { activity: string; activityMin: string };
  activity: Activity | '';
  minutes: string;
  onActivity: (value: Activity | '') => void;
  onMinutes: (value: string) => void;
}): React.JSX.Element {
  const { t } = useTranslation();
  return (
    <div className="flex flex-wrap gap-2">
      <label htmlFor={ids.activity} className="flex min-w-40 flex-1 flex-col gap-1">
        <span className="kw-label">{t('calendar.editor.activity')}</span>
        <select
          id={ids.activity}
          value={activity}
          onChange={(event) => onActivity(event.target.value as Activity | '')}
          className="kw-field"
        >
          <option value="">{t('calendar.editor.noActivity')}</option>
          {ACTIVITY_KEYS.filter((key) => key !== 'strength').map((key) => (
            <option key={key} value={key}>
              {t(`activity.${key}`)}
            </option>
          ))}
        </select>
      </label>
      {activity ? (
        <label htmlFor={ids.activityMin} className="flex w-28 flex-col gap-1">
          <span className="kw-label">{t('calendar.editor.activityMin')}</span>
          <input
            id={ids.activityMin}
            inputMode="numeric"
            value={minutes}
            onChange={(event) => onMinutes(event.target.value)}
            className="kw-field"
          />
        </label>
      ) : null}
      <p className="w-full text-xs text-ink-muted">{t('calendar.editor.activityWhy')}</p>
    </div>
  );
}
